import React, { useState } from 'react';
import { useCustomers, Customer } from '@/hooks/useCustomers';
import { CustomerSourcesList } from './CustomerSourcesList';
import { CustomerAllSourcesView } from './CustomerAllSourcesView';

export const MultiSourceManager = () => {
  const { customers, isLoading } = useCustomers();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSourceType, setActiveSourceType] = useState('all');

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Source types available for each customer
  const sourceTypes = [
    { id: 'mailbox', name: 'Mailbox', icon: '📧', default: true, description: 'Default email forwarding' },
    { id: 'portal', name: 'Portal Integration', icon: '🏢', default: false, description: 'Utility company portals' },
    { id: 'bulk', name: 'Bulk Upload', icon: '📁', default: false, description: 'Manual file uploads' },
    { id: 'drive', name: 'Drive Sync', icon: '☁️', default: false, description: 'Google/OneDrive integration' },
    { id: 'smtp', name: 'SMTP', icon: '📨', default: false, description: 'Direct email server' },
    { id: 'api', name: 'API Integration', icon: '🔗', default: false, description: 'Custom API connections' },
    { id: 'manual', name: 'Manual Entry', icon: '✍️', default: false, description: 'Manual bill input' }
  ];

  if (isLoading) {
    return (
      <div className="h-full bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-muted-foreground">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-muted">
      {/* Header with Source Type Tabs */}
      <div className="bg-card border-b">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Customer Data Sources</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage all data collection sources for your customers across multiple channels
              </p>
            </div>
            <div className="flex gap-2">
              <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90">
                + Add New Customer
              </button>
              <button className="border border-border text-foreground px-4 py-2 rounded-lg hover:bg-muted">
                Bulk Operations
              </button>
            </div>
          </div>

          {/* Source Type Filter Tabs */}
          <div className="flex gap-1 overflow-x-auto">
            <button
              onClick={() => setActiveSourceType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                activeSourceType === 'all' 
                  ? 'bg-primary/10 text-primary border border-primary/20' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              📊 All Sources
            </button>
            {sourceTypes.map(source => (
              <button
                key={source.id}
                onClick={() => setActiveSourceType(source.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  activeSourceType === source.id 
                    ? 'bg-primary/10 text-primary border border-primary/20' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {source.icon} {source.name}
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 pb-4">
          <input
            type="text"
            placeholder="🔍 Search customers by name or email..."
            className="w-full max-w-md px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-full">
        <CustomerSourcesList
          customers={filteredCustomers}
          selectedCustomer={selectedCustomer}
          onSelectCustomer={setSelectedCustomer}
          activeSourceType={activeSourceType}
          sourceTypes={sourceTypes}
        />

        <CustomerAllSourcesView 
          customer={selectedCustomer}
          activeSourceType={activeSourceType}
          sourceTypes={sourceTypes}
        />
      </div>
    </div>
  );
};